import React, { useContext } from "react";
import { FontSize, Spacing } from "../../theme/Theme";
import { renderRBPayText } from "../RBPayWordmark";
import { shouldShowRBPayNotSetupNote } from "../../util/rbPay";
import { RBContext } from "../RBContext";

/**
 * US6559 Phase 2 — the standing advisory note that REPLACES the popup we previously planned
 * for the "office not on RB Pay" case. The client asked for on-screen copy the worker reads
 * before applying, rather than a modal they have to dismiss.
 *
 * IMPORTANT: this does not replace the three Phase 1 popups. Those cover the opposite
 * situation (the office IS on RB Pay but the worker is not set up) and remain in place.
 *
 * Shown only when all three spec conditions hold — see shouldShowRBPayNotSetupNote:
 *   shift is not network paid, location is not on RB Pay, and the NETWORK has RB Pay
 *   switched on for locations.
 * Placement is the caller's job: Shift Details / Apply only, between the Terms of Service
 * checkbox and the Apply CTAs. Never on cards, never on future/confirmed shifts (the copy
 * says "if you are selected for this shift", which no longer applies once they have been).
 */
const PREFIX = "This office is not setup for RB Pay, ";
const EMPHASIS = "they will pay you directly,";
const SUFFIX =
  " please be prepared with your preferred payment information if you are selected for this shift.";

interface RBPayNotSetupNoteProps {
  shift?: any;
  /** Overrides the internal derivation when the caller already resolved it. */
  visible?: boolean;
  style?: React.CSSProperties;
}

export const RBPayNotSetupNote: React.FC<RBPayNotSetupNoteProps> = ({
  shift,
  visible,
  style,
}) => {
  // Border/text follow the network's brand colour rather than a hardcoded orange, so the
  // note matches whichever network the worker is browsing.
  const { primaryColor } = useContext(RBContext);
  const shouldShow =
    visible !== undefined ? visible : shouldShowRBPayNotSetupNote(shift);

  if (!shouldShow) {
    return null;
  }

  return (
    <div
      style={{
        border: `1px solid ${primaryColor}`,
        borderRadius: 6,
        padding: Spacing.small,
        color: primaryColor,
        fontSize: FontSize.default,
        ...style,
      }}
    >
      {renderRBPayText(PREFIX)}
      <strong>{EMPHASIS}</strong>
      {SUFFIX}
    </div>
  );
};
